import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Verificar se há token salvo
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
    }

    setLoading(false);
  }, []);

  const login = async (email, senha, id_cidade) => {
    try {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('Tentando login:', { email, id_cidade });
      }
      
      // Garantir que id_cidade seja número
      const cidadeId = id_cidade ? parseInt(id_cidade) : null;
      
      const response = await api.post('/auth/login', { 
        email: email.trim(), 
        senha, 
        id_cidade: cidadeId 
      });
      
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('Resposta do login:', response.data);
      }
      
      // Verificar se a resposta tem a estrutura esperada
      if (!response.data || !response.data.success) {
        return {
          success: false,
          message: response.data?.message || 'Erro ao fazer login'
        };
      }

      const { token, usuario } = response.data;

      if (!token || !usuario) {
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.error('Resposta inválida:', response.data);
        }
        return {
          success: false,
          message: 'Resposta inválida do servidor'
        };
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(usuario));
      if (cidadeId) {
        localStorage.setItem('cidade_selecionada', cidadeId.toString());
      }
      setToken(token);
      setUser(usuario);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.log('Login realizado com sucesso');
      }
      return { success: true };
    } catch (error) {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === 'development') {
        console.error('Erro no login:', error);
        console.error('Detalhes do erro:', {
          response: error.response?.data,
          status: error.response?.status,
          message: error.message
        });
      }
      
      // Tratar diferentes tipos de erro
      if (error.response) {
        // Erro com resposta do servidor
        const errorMessage = error.response.data?.message || 
                           error.response.data?.errors?.[0]?.msg || 
                           'Erro ao fazer login';
        return {
          success: false,
          message: errorMessage
        };
      } else if (error.request) {
        // Erro de conexão
        return {
          success: false,
          message: 'Não foi possível conectar ao servidor. Verifique se o backend está rodando.'
        };
      } else {
        // Outro tipo de erro
        return {
          success: false,
          message: error.message || 'Erro ao fazer login'
        };
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  // Verificar autenticação baseado no estado e localStorage
  const checkAuth = () => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (storedToken && storedUser && !user) {
      // Recarregar usuário do localStorage se não estiver no estado
      try {
        setUser(JSON.parse(storedUser));
        setToken(storedToken);
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch (e) {
        // Log apenas em desenvolvimento
        if (process.env.NODE_ENV === 'development') {
          console.error('Erro ao carregar usuário do localStorage:', e);
        }
      }
    }
    return !!(token && user) || !!(storedToken && storedUser);
  };

  const isAuthenticated = checkAuth();

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};

